import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogContent,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Backdrop, CircularProgress } from "@mui/material";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { errorToast, handleError } from "../../../utils/common";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  address: Yup.string().required("Address is required"),
  pincode: Yup.string()
    .length(6, "Pincode must be 6 digits")
    .required("Pincode is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  contact_no1: Yup.string()
    .trim()
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required")
    .matches(/^[6-9]\d{9}$/, "Invalid phone number"),
  contact_no2: Yup.string()
    .trim()
    .min(10, "Phone number must be at least 10 digits")
    .matches(/^[6-9]\d{9}$/, "Invalid phone number"),
  email: Yup.string()
    .trim()
    .email("Enter a valid email address")
    .required("Email is required")
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      "Invalid email address",
    ),
  gst_no: Yup.string()
    .trim()
    .length(15, "GST number must be 15 characters")
    .required("GST number is required")
    .matches(/^[0-9A-Z]{15}$/, "Invalid GST number"),
  business_pan_no: Yup.string()
    .trim()
    .length(10, "PAN number must be 10 characters")
    .required("PAN number is required")
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Invalid PAN number"),
});

const initialValues = {
  distributor_code: "",
  name: "",
  gst_name: "",
  address: "",
  pincode: "",
  city: "",
  state: "",
  contact_no1: "",
  contact_no2: "",
  email: "",
  gst_no: "",
  business_pan_no: "",
};

export function BorrowerFormModal({ open, onClose, onSubmit }: any) {
  const privateInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);

  const getGstData = async (val: string, setFieldValue: any) => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V1}/orders/pharmacy/validate-gst?gstin=${val}`,
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        setFieldValue("name", result.data.response?.business_name || "");
        setFieldValue("gst_name", result.data.response?.business_name || "");
        setFieldValue("address", result.data.response?.address || "");
        setFieldValue("pincode", result.data.response?.pincode || "");
        if (result.data.response?.pincode) {
          getPincodeData(result.data.response?.pincode, setFieldValue);
        }
      } else {
        errorToast(result?.data?.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const getPincodeData = async (val: string, setFieldValue: any) => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V3}/search/${val}`,
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        const city = result.data.response?.city || "";
        const state = result.data.response?.state || "";
        const formattedCity = city.charAt(0).toUpperCase() + city.slice(1);
        const formattedState = state.charAt(0).toUpperCase() + state.slice(1);
        setFieldValue("city", formattedCity);
        setFieldValue("state", formattedState);
      } else {
        errorToast(result?.data?.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 2,
        }}
      >
        <CircularProgress sx={{ color: "#0058ff" }} />
      </Backdrop>

      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            display: "flex",
          },
        }}
        BackdropProps={{
          sx: {
            "&.MuiBackdrop-root": {
              inert: open,
            },
          },
        }}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={onClose}>
          Add Borrower - Business Details
        </CustomDialogTitle>
        <DialogContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form noValidate>
                <Grid container spacing={3}>
                  <Grid mt={1.5} container item xs={12} sm={6}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <TextField
                        size="small"
                        fullWidth
                        name="gst_no"
                        label="GST Number"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.gst_no && Boolean(errors.gst_no)}
                        helperText={touched.gst_no && errors.gst_no}
                        required
                      />
                      <Button
                        sx={{ minWidth: "fit-content" }}
                        variant="outlined"
                        disabled={
                          loading || values?.gst_no?.trim()?.length !== 15
                        }
                        onClick={() => getGstData(values.gst_no, setFieldValue)}
                      >
                        Validate
                      </Button>
                    </Box>
                  </Grid>
                  <Grid mt={1.5} container item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      name="name"
                      label="Name"
                      value={values?.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12}>
                    <TextField
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      name="address"
                      label="Address"
                      value={values?.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address && Boolean(errors.address)}
                      helperText={touched.address && errors.address}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="pincode"
                      label="Pincode"
                      value={values?.pincode}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleChange(e);
                        if (val.length === 6) {
                          getPincodeData(val, setFieldValue);
                        } else {
                          setFieldValue("city", "");
                          setFieldValue("state", "");
                        }
                      }}
                      onBlur={handleBlur}
                      error={touched.pincode && Boolean(errors.pincode)}
                      helperText={touched.pincode && errors.pincode}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="city"
                      label="City"
                      value={values?.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.city && Boolean(errors.city)}
                      helperText={touched.city && errors.city}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="state"
                      label="State"
                      value={values?.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.state && Boolean(errors.state)}
                      helperText={touched.state && errors.state}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      name="contact_no1"
                      label="Phone Number (1)"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.contact_no1 && Boolean(errors.contact_no1)}
                      helperText={touched.contact_no1 && errors.contact_no1}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      name="contact_no2"
                      label="Phone Number (2)"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.contact_no2 && Boolean(errors.contact_no2)}
                      helperText={touched.contact_no2 && errors.contact_no2}
                    />
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      name="email"
                      label="Primary Email Address"
                      value={values?.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      name="business_pan_no"
                      label="PAN Number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.business_pan_no &&
                        Boolean(errors.business_pan_no)
                      }
                      helperText={
                        touched.business_pan_no && errors.business_pan_no
                      }
                      required
                    />
                  </Grid>

                  <Grid container item xs={12}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Button size="small" variant="outlined" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        type="submit"
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
